import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import AiScreeningNotice from "../../../components/AiScreeningNotice";
import CourseCertificatesTable from "../../../components/CourseCertificatesTable";
import CourseCertificateUploadErrors from "../../../components/CourseCertificateUploadErrors";
import CleButton from "../../../components/CleButton";
import DropZoneFeedback from "../../../components/DropZoneFeedback";
import RoundedCard from "../../../components/RoundedCard";
import { useCourseCertificates } from "@/hooks/useCourseCertificates";
import { useCourseCertificateBatchUpload } from "@/hooks/useCourseCertificateBatchUpload";
import { isPdfFile, useFileDropZone } from "../../../hooks/useFileDropZone";
import { useInvalidateReportingStatus } from "../../../hooks/useInvalidateReportingStatus";
import type {} from "../../../models/courseCertificate";
import { isAnalyzingStatus } from "../../../models/evidenceItem";
import CourseCertificateUploadDialog from "../../home/components/CourseCertificateUploadDialog";

type CourseCertificatesDocumentTabProps = {
  detailPathPrefix?: string;
  description: string | null;
  reported?: boolean | null;
  showStatusColumn?: boolean;
  title: string;
};

const COURSE_CERTIFICATES_PAGE_SIZE = 10;
const INVALID_DROP_MESSAGE = "Slip kun PDF-filer for at uploade kursusbeviser.";

const CourseCertificatesDocumentTab = ({
  detailPathPrefix = "",
  description,
  reported,
  showStatusColumn = true,
  title,
}: CourseCertificatesDocumentTabProps) => {
  const [page, setPage] = useState(1);
  const hadAnalyzingCourseCertificatesRef = useRef(false);
  const navigate = useNavigate();
  const invalidateReportingStatus = useInvalidateReportingStatus();
  const {
    cancel: cancelUpload,
    errors: uploadErrors,
    isUploading,
    receivedFiles,
    settledFiles,
    skippedFiles,
    start: startUpload,
    status: uploadStatus,
    totalFiles,
  } = useCourseCertificateBatchUpload();
  const {
    data: response,
    isError,
    isFetching,
    isLoading,
  } = useCourseCertificates({
    page,
    page_size: COURSE_CERTIFICATES_PAGE_SIZE,
    reported,
  });

  const hasAnalyzingCourseCertificates =
    response?.items.some((certificate) =>
      isAnalyzingStatus(certificate.status),
    ) ?? false;

  useEffect(() => {
    if (hasAnalyzingCourseCertificates) {
      hadAnalyzingCourseCertificatesRef.current = true;

      return;
    }

    if (hadAnalyzingCourseCertificatesRef.current) {
      invalidateReportingStatus();
      hadAnalyzingCourseCertificatesRef.current = false;
    }
  }, [hasAnalyzingCourseCertificates, invalidateReportingStatus]);

  const handleCourseCertificateClick = (certificateId: string) => {
    void navigate(`${detailPathPrefix}kursusbevis/${certificateId}`);
  };

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      // Rows are revealed newest-first on page 1; without this a user on
      // page 3 sees no progress at all.
      setPage(1);
      startUpload(files);
    },
    [startUpload],
  );

  const { dropError, dropZoneProps, isDragActive } = useFileDropZone({
    acceptFile: isPdfFile,
    disabled: isUploading,
    invalidDropMessage: INVALID_DROP_MESSAGE,
    onFilesDrop: handleFilesSelected,
  });

  const uploadSummary =
    uploadStatus === "cancelled" ? (
      <>
        <span className="fw-semibold">Uploaden blev afbrudt.</span>{" "}
        {receivedFiles} kursusbeviser blev modtaget.
        {skippedFiles > 0
          ? " Nogle af de igangværende filer kan også være uploadet — tjek oversigten nedenfor."
          : null}
      </>
    ) : uploadStatus === "done" && receivedFiles > 0 ? (
      <>
        {receivedFiles} af {totalFiles} kursusbeviser blev modtaget og
        analyseres nu.
      </>
    ) : null;

  return (
    <RoundedCard
      className={[
        "cle-rounded-card--drop-target",
        isDragActive ? "cle-rounded-card--drag-active" : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
      bodyClassName="p-0 mt-3"
      header={title}
      description={
        // Oplysningen står på kortet og ikke kun i upload-dialogen, fordi PDF'er
        // også kan trækkes direkte ind her uden at dialogen åbnes.
        <>
          {description ? <>{description} </> : null}
          <AiScreeningNotice subject="Kursusbeviser" variant="short" />
        </>
      }
      action={
        <CourseCertificateUploadDialog
          isUploading={isUploading}
          onFilesSelected={handleFilesSelected}
        />
      }
      aria-busy={isUploading}
      {...dropZoneProps}
    >
      <DropZoneFeedback
        actionError={
          uploadErrors.length > 0 ? (
            <CourseCertificateUploadErrors
              errors={uploadErrors}
              title={`${uploadErrors.length} ${
                uploadErrors.length === 1 ? "fil" : "filer"
              } kunne ikke uploades:`}
            />
          ) : null
        }
        actionSuccess={uploadSummary}
        actionStatus={
          isUploading ? (
            <div className="d-flex align-items-center gap-3 w-100 min-w-0">
              <span className="fw-semibold flex-grow-1 min-w-0">
                Uploader {settledFiles} af {totalFiles} kursusbeviser...
              </span>
              <CleButton
                id="cle-cancel-course-certificate-batch-upload"
                variant="outline"
                size="xs"
                onClick={cancelUpload}
              >
                Afbryd upload
              </CleButton>
            </div>
          ) : null
        }
        dropError={dropError}
        dropMessage="Slip PDF-filer her for at uploade"
        isDragActive={isDragActive}
      />
      <CourseCertificatesTable
        certificates={response?.items ?? []}
        isError={isError}
        isLoading={isLoading}
        onCertificateClick={handleCourseCertificateClick}
        showStatusColumn={showStatusColumn}
        pagination={
          response
            ? {
                isDisabled: isFetching,
                onPageChange: setPage,
                page: response.page,
                total: response.total,
                totalPages: response.totalPages,
              }
            : undefined
        }
      />
    </RoundedCard>
  );
};

export default CourseCertificatesDocumentTab;
